import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getListSelectorIteratorMaxOptions } from '@/utils/listNode';

type TextIteratorValue = { options: string[] };

export function useTextIterator({
  value,
  onChange,
}: {
  value: TextIteratorValue;
  onChange: (value: TextIteratorValue) => void;
}) {
  const options = useMemo(() => value?.options ?? [''], [value?.options]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(null);
  const maxOptions = getListSelectorIteratorMaxOptions();

  const emit = useCallback((newOptions: string[]) => onChange({ options: newOptions }), [onChange]);

  const handleValueChange = useCallback(
    (val: string, index: number) => {
      const next = options.map((o, i) => (i === index ? val : o));
      emit(next);
    },
    [options, emit],
  );

  const handleAddItem = useCallback(() => {
    emit([...options, '']);
    setPendingFocusIndex(options.length);
  }, [options, emit]);

  const handleEnter = useCallback(
    (index: number) => {
      if (options.length >= maxOptions) return;
      const next = [...options.slice(0, index + 1), '', ...options.slice(index + 1)];
      emit(next);
      setPendingFocusIndex(index + 1);
    },
    [options, maxOptions, emit],
  );

  const handleTab = useCallback(
    (index: number, direction: 'next' | 'prev') => {
      if (options.length === 0) return;
      const jump = direction === 'next' ? 1 : -1;
      setPendingFocusIndex((index + jump + options.length) % options.length);
    },
    [options.length],
  );

  const handleDelete = useCallback((index: number) => emit(options.filter((_, i) => i !== index)), [options, emit]);

  const handleDeleteAndFocusPrev = useCallback(
    (index: number) => {
      setPendingFocusIndex(Math.max(0, index - 1));
      handleDelete(index);
    },
    [handleDelete],
  );

  useLayoutEffect(() => {
    if (pendingFocusIndex == null) return;
    const el = inputRefs.current[pendingFocusIndex];
    if (el) {
      el.focus();
      el.select();
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, options]);

  return {
    options,
    maxOptions,
    inputRefs,
    handleValueChange,
    handleAddItem,
    handleEnter,
    handleTab,
    handleDelete,
    handleDeleteAndFocusPrev,
  };
}
